import { useCallback, useMemo, useState, type ReactNode } from 'react'

import { sameResourceId } from '@krp/client-runtime'
import {
  VatSettingsScreen,
  VatSourceCard,
  platformCatalogProducts,
  platformMyResources,
} from '@krp/platform-ui'

import { NOMENCLATURE_SETTINGS_CARDS } from '../../composition/shell'
import { messageOf, noEdit, useReloadOn } from './vatCardKit'
import { localSourceVm } from './vatSettingsModel'

const READ = 'catalog.read'
const WRITE = 'catalog.write'

/**
 * Binding «Номенклатура → Настройка» (ставка НДС): по карточке на каждый источник, где человеку разрешено читать каталог
 * (база 1С, кабинет AQSI), и карточка общего каталога Platform. Что источник не отдаёт ставку — говорится словами («нет данных»).
 */
export function NomenclatureSettingsBinding() {
  const readable = platformMyResources.useQuery({ capability: READ })
  const writable = platformMyResources.useQuery({ capability: WRITE })
  const [reloadToken, setReloadToken] = useState(0)
  const reloadAll = useCallback(() => setReloadToken((token) => token + 1), [])

  const sources = readable.data ?? []
  const cards = useMemo(() => {
    const result: { id: string; node: ReactNode }[] = []
    for (const resource of readable.data ?? []) {
      const Card = NOMENCLATURE_SETTINGS_CARDS[resource.kind]
      if (Card === undefined) continue
      const canWrite = (writable.data ?? []).some((item) => sameResourceId(item.id, resource.id))
      result.push({ id: resource.id, node: <Card resource={resource} canWrite={canWrite} reloadToken={reloadToken} /> })
    }
    result.push({ id: 'local', node: <LocalVatCard reloadToken={reloadToken} /> })
    return result
  }, [readable.data, writable.data, reloadToken])

  const emptyText =
    readable.isSuccess && sources.length === 0
      ? 'Нет источников, где вам разрешено читать каталог: ни база 1С, ни кабинет. Права выдаёт администратор.'
      : null
  return <VatSettingsScreen cards={cards} onReloadAll={reloadAll} emptyText={emptyText} />
}

function LocalVatCard({ reloadToken }: { reloadToken: number }) {
  const result = platformCatalogProducts.useQuery({ query: '', page: 1, pageSize: 1 })
  useReloadOn(reloadToken, () => void result.refetch())
  const vm = localSourceVm({
    total: result.data?.total ?? null,
    query: { isLoading: result.isFetching, isError: result.isError, errorMessage: result.error === undefined ? null : messageOf(result.error) },
  })
  return <VatSourceCard vm={vm} actions={noEdit(() => void result.refetch())} />
}
